import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { chatActions, rtcBufferActions } from '../../redux/actions/';
import { ChatBox, ChatLog, ChatRawMessage, ChatRawCommand, ChatSend, MessageInput, DiceSend, DiceBlock, DiceButton, IsTyping } from './ChatBox.styles';
import T from '../i18n/i18n';
import ChatMessage from './ChatMessage/ChatMessage';
import Audio from '../../services/audioCache';
import { Message, ParseMessage } from './Message';

const jumpToEnd = (element) => {
    var len = element.value.length;
    if (element.setSelectionRange) {
        element.focus();
        element.setSelectionRange(len, len);
    } else if (element.createTextRange) {
        var t = element.createTextRange();
        t.collapse(true);
        t.moveEnd('character', len);
        t.moveStart('character', len);
        t.select();
    }
}

export default () => {
    const [messge, setMessage] = useState('');
    const [messagesHistory, setMessagesHistory] = useState([]);
    const [historyCounter, setHistoryCounter] = useState(-1);
    const [showIsTyping, setShowIsTyping] = useState(false);

    const messages = useSelector(state => state.chatState.messages);
    const typing = useSelector(state => state.chatState.typing);
    const email = useSelector(state => state.authState.email);
    const user = useSelector(state => state.authState.name);
    const color = useSelector(state => state.authState.color);
    
    const isEnd = useRef(true);
    const diceBlockRef = useRef(null);
    const chatScrollRef = useRef(null);
    const typingInterval = useRef(null);
    const typingTimout = useRef(null);
    const hideTypingTimout = useRef(null);
    const inputRef = useRef(null);
    const dispatch = useDispatch();

    let onScroll = () => {
        let scrollTop = chatScrollRef.current.scrollTop;
        let scrollHeight = chatScrollRef.current.scrollHeight;
        let offsetHeight = chatScrollRef.current.offsetHeight;

        isEnd.current = Math.ceil(scrollTop) >= (scrollHeight - offsetHeight - 50);
    };

    useEffect(() => {
        if (typing.length > 0) {
            setShowIsTyping(true);
        } else {
            setShowIsTyping(false);
        }

        if (hideTypingTimout.current) {
            clearTimeout(hideTypingTimout.current);
            hideTypingTimout.current = null;
        }

        hideTypingTimout.current = setTimeout(() => {
            setShowIsTyping(false);
        },1050);

    }, [typing]);

    useEffect(() => {
        if (!chatScrollRef.current) {
            return;
        }

        chatScrollRef.current.addEventListener('scroll', onScroll);

        let scrollRef = chatScrollRef.current;
        return () => {
            scrollRef.removeEventListener('scroll', onScroll);
        }
    }, []);

    useEffect(() => {
        if (!chatScrollRef.current) {
            return;
        }
        
        if (isEnd.current) {
            chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        }

        let lastMessage = messages[messages.length - 1];
        if (lastMessage) {
            let command = lastMessage.messages[lastMessage.messages.length - 1].command;
            if (['roll','totalroll','silenttotalroll','silentroll', 'multiroll', 'hiddenroll'].indexOf(command) >= 0) {
                (async () => {
                    Audio.play('dice');
                })();
            }
        }
    }, [messages]);

    useEffect(() => {
        jumpToEnd(inputRef.current);
    }, [historyCounter]);

    const sendMessage = (forcedMessage) => {
        let message = forcedMessage || messge;
        
        const {content, command} = ParseMessage(message);
        const msg = Message(email, user, color, command, content);

        dispatch(chatActions.addMessage(msg));
        dispatch(rtcBufferActions.pushOutputBuffer({
            type: 'chatMessage',
            data: JSON.stringify(msg)
        }));

        setMessage('');
    }

    const handleKeydown = (e) => {
        if (e.which === 38) {
            let history = historyCounter;
            if ((history + 1) < messagesHistory.length) {
                history += 1;
            }

            setMessage(messagesHistory[history] || '');
            setHistoryCounter(history);
        } else if (e.which === 40) {
            let history = historyCounter;
            if ((history - 1) >= -1) {
                history -= 1;
            }

            setMessage(messagesHistory[history] || '');
            setHistoryCounter(history);
        }
    }

    const handleKeypress = (e) => {
        if(e.which === 13 && messge.length > 0) {
            setMessagesHistory(old => [messge, ...old]);
            setHistoryCounter(-1);
            sendMessage();

            if (typingTimout.current) {
                clearTimeout(typingTimout.current);
                typingTimout.current = null;
            }

            if (typingInterval.current) {
                clearInterval(typingInterval.current);
                typingInterval.current = null;
            }
        } else {

            if (!typingInterval.current) {
                dispatch(rtcBufferActions.pushOutputBuffer({
                    type: 'typingMessage',
                    data: {user}
                }));

                typingInterval.current = setInterval(() => {
                    dispatch(rtcBufferActions.pushOutputBuffer({
                        type: 'typingMessage',
                        data: {user}
                    }));
                    return;
                }, 1000);
            }

            if (typingTimout.current) {
                clearTimeout(typingTimout.current);
                typingTimout.current = null;
            }

            typingTimout.current = setTimeout(() => {
                clearInterval(typingInterval.current);
                typingInterval.current = null;
                return;
            }, 950);
        }
    }

    /*const handleButtonSend = (e) => {
        if(messge.length > 0) {
            setMessagesHistory(old => [messge, ...old]);
            setHistoryCounter(-1);
            sendMessage();
        }
    }*/

    const openDices = () => {
        if (diceBlockRef.current) {
            diceBlockRef.current.classList.toggle('hide');
        }
    }

    const rollDice = (sides) => {
        sendMessage(`@r 1d${sides}`);
        openDices();
    }

    const keyCancel = (e) => {
        e.stopPropagation();
    }

    return <ChatBox onKeyDown={keyCancel} >
        <ChatLog className="scrollbar" ref={chatScrollRef}>
            <ChatRawMessage><T value="chat.howuse" /></ChatRawMessage>
            <ChatRawMessage><T value="chat.howroll" /></ChatRawMessage>
            <ChatRawCommand>
                <span>@roll 1d20</span><br />
                <span>@r 1d8+3</span><br />
                <span>@r 3d6</span><br />
            </ChatRawCommand>
            {messages.map((message, index) => <ChatMessage key={index} message={message} />)}
        </ChatLog>
        <IsTyping className={showIsTyping ? '' : 'hidden'}>{typing[0] || ''} <T value={'chat.istyping'} /></IsTyping>
        <ChatSend>
            <MessageInput value={messge} onChange={(e)=>{setMessage(e.target.value)}} onKeyPress={handleKeypress} onKeyDown={handleKeydown} ref={inputRef} />
            <DiceSend onClick={() => {openDices()}} />
            {/*<MessageSend onClick={() => {handleButtonSend()}} />*/}
            <DiceBlock className={'hide'} ref={diceBlockRef}>
                <DiceButton onClick={() => {rollDice(4)}} icon={'\\F05EB'}></DiceButton>
                <DiceButton onClick={() => {rollDice(6)}} icon={'\\F05ED'}></DiceButton>
                <DiceButton onClick={() => {rollDice(8)}} icon={'\\F05EC'}></DiceButton>
                <DiceButton onClick={() => {rollDice(10)}} icon={'\\F076F'}></DiceButton>
                <DiceButton onClick={() => {rollDice(12)}} icon={'\\F0867'}></DiceButton>
                <DiceButton onClick={() => {rollDice(20)}} icon={'\\F05EA'}></DiceButton>
                <DiceButton onClick={() => {rollDice(100)}} icon={'\\F1156'}></DiceButton>
                <DiceButton onClick={() => {openDices()}} icon={'\\F0156'}></DiceButton>
            </DiceBlock>
        </ChatSend>
    </ChatBox>;
}